import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import { useZEElementTypes } from "@/features/ze_tenders/hooks";
import { elementSwatchSvg } from "@/features/ze_tenders/utils";
import type { ZEElementType } from "@/features/ze_tenders/types";
import {
  useDxfLayerMutations,
  useDxfLayerPresets,
  useDxfLayers,
  useDxfPresetMutations,
  useTaskPolling,
} from "../hooks";
import { reloadPresetProjects } from "../api";
import { apiErr } from "../utils";
import type { DxfLayerCatalogEntry, DxfLayerKind, DxfLayerPreset, DxfLayerStatus } from "../types";
import { EmptyState, SectionCard, SectionToolbar } from "../components/ui";

const KIND_LABEL: Record<DxfLayerKind, string> = {
  line: "Linia (trasa)",
  object: "Obiekt (punkt)",
};

// Legacy free-form hints for the semantic_type field — the operator can type anything.
// ZE catalog types (codes + preset_keys) are appended dynamically; matching one of them
// gives the layer the tender styling (colour/dash/symbol) on the project map.
const SEMANTIC_SUGGESTIONS = [
  "trasa_kablowa", "trasa_napowietrzna", "slup", "zlacze", "stacja", "mufa", "przylacze",
];

// Status techniczny warstwy. To POLE, a nie nazwa sluga, decyduje o doborze formularzy
// zgody WPW (projektowane → 4a, likwidowane → 4b, modernizowane → 4c), o warstwie
// demontażu na mapie i o statystykach montaż/demontaż.
const STATUS_OPTIONS: { value: DxfLayerStatus; label: string }[] = [
  { value: "", label: "— (z nazwy)" },
  { value: "projektowane", label: "Projektowane (budowa)" },
  { value: "likwidowane", label: "Likwidowane (demontaż)" },
  { value: "modernizowane", label: "Modernizowane / przebudowa" },
  { value: "istniejace", label: "Istniejące (nawiązanie)" },
];

// Lustro `apps.geo.layer_status.infer_status` — podpowiedź „co system sam odczyta",
// gdy pole statusu jest puste. Kolejność ta sama: demontaż i modernizacja przed
// istniejącym/projektowanym („likwidacja istniejącego słupa" to demontaż).
const STATUS_MARKERS: [string[], string][] = [
  [["likwid", "demonta", "dmontaz", "rozbior"], "likwidowane"],
  [["moderniz", "przebud", "rozbudow", "wymian"], "modernizowane"],
  [["istniej", "istn_", "_istn", "nawiazan"], "istniejace"],
  [["projektowan", "proj_", "_proj", "budowa"], "projektowane"],
];

function inferStatusLabel(...texts: string[]): string {
  for (const text of texts) {
    const hay = (text || "").toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    if (!hay) continue;
    for (const [needles, status] of STATUS_MARKERS) {
      if (needles.some((n) => hay.includes(n))) return `odczyt z nazwy: ${status}`;
    }
  }
  return "nazwa nie mówi — WPW przyjmie „projektowane”";
}

// Semantic value → ZE element type, accepting both the internal code (ziemna_nn_proj)
// and the human preset_key (projektowana_ziemna_nn) — the map styles both identically.
function buildEtLookup(elementTypes: ZEElementType[] | undefined): Map<string, ZEElementType> {
  const m = new Map<string, ZEElementType>();
  for (const t of elementTypes ?? []) {
    m.set(t.code, t);
    if (t.preset_key) m.set(t.preset_key, t);
  }
  return m;
}

// Inline verdict under the semantic input: matched ZE type (swatch + label) or a warning
// that the value is outside the ZE catalog (layer stays unstyled on the map).
function SemanticVerdict({ value, etLookup }: { value: string; etLookup: Map<string, ZEElementType> }) {
  const v = value.trim();
  if (!v) return null;
  const et = etLookup.get(v);
  if (et) {
    return (
      <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]">
        <span dangerouslySetInnerHTML={{ __html: elementSwatchSvg(et) }} /> {et.label}
      </span>
    );
  }
  return (
    <span className="mt-0.5 block text-[10px] leading-snug text-amber-600">
      Poza katalogiem typów ZE — warstwa bez stylu przetargowego na mapie.
    </span>
  );
}

// ── reload-projects dialog ────────────────────────────────────────────────────
// Re-recognise every CURRENT route version using this preset from its stored DXF —
// picks up layers just mapped/edited (route, objects, unmapped-layer report) and re-runs
// ULDK, no per-project re-upload. Director/CEO only (mirrors the backend gate).
function ReloadPresetProjectsDialog({ preset, onClose }: { preset: DxfLayerPreset; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  const running = !!taskId && (!status || ["pending", "running"].includes(status));

  const start = async () => {
    setError(null);
    try {
      const r = await reloadPresetProjects(preset.id);
      setTaskId(r.task_id);
    } catch (e) {
      setError(apiErr(e));
    }
  };

  return (
    <Modal open onClose={onClose} title={`Przeładuj projekty z presetu „${preset.name}”`}>
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-text-secondary)] leading-snug">
          Rozpoznaje ponownie wszystkie <span className="font-semibold text-[var(--color-text-primary)]">aktualne</span>{" "}
          wersje tras korzystające z tego presetu — z już wgranych plików DXF, bez wrzucania map od nowa.
          Wykrywa warstwy dodane/poprawione w presecie (trasa, obiekty, lista niezmapowanych) i przelicza
          działki (ULDK). Renderuje sekwencyjnie i odpytuje ULDK per projekt, więc może potrwać dłuższą chwilę.
        </p>
        {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}
        {taskId && task.data && (
          <div className="space-y-1.5">
            <p className="text-xs font-mono text-[var(--color-text-secondary)]">
              {status === "success"
                ? "Zakończono."
                : status === "failure"
                  ? "Błąd — szczegóły w logu poniżej."
                  : "W toku… może potrwać dłuższą chwilę."}
            </p>
            <pre className="max-h-56 overflow-y-auto text-[11px] font-mono whitespace-pre-wrap bg-[var(--color-bg-elevated)] border border-[var(--color-border)] p-2">
              {task.data.log || "…"}
            </pre>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          {running && (
            <span className="text-[11px] font-mono text-[var(--color-text-muted)] mr-auto self-center">
              Możesz zamknąć — przeładowanie działa dalej w tle.
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Zamknij</Button>
          {!taskId && <Button size="sm" onClick={start}>Rozpocznij</Button>}
        </div>
      </div>
    </Modal>
  );
}

// ── preset create / edit modal ───────────────────────────────────────────────
function PresetModal({ preset, onClose }: { preset: DxfLayerPreset | null; onClose: () => void }) {
  const m = useDxfPresetMutations();
  const [name, setName] = useState(preset?.name ?? "");
  const [description, setDescription] = useState(preset?.description ?? "");
  const mutation = preset ? m.update : m.create;

  const submit = () => {
    if (!name.trim()) return;
    const payload = { name: name.trim(), description: description.trim() };
    if (preset) m.update.mutate({ id: preset.id, payload }, { onSuccess: onClose });
    else m.create.mutate(payload, { onSuccess: onClose });
  };

  return (
    <Modal open onClose={onClose} title={preset ? "Edytuj preset" : "Nowy preset warstw DXF"}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Nazwa presetu *</span>
          <input
            className="input-base mt-1"
            value={name}
            autoFocus
            placeholder="np. ENERGA — standard"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="section-label">Opis (podpowiedź dla projektanta)</span>
          <input
            className="input-base mt-1"
            value={description}
            placeholder="np. mapy z biura X / ENERGA Gdańsk"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {mutation.isError && (
          <p className="text-xs text-[var(--color-primary)] font-mono">
            Nie udało się zapisać presetu (nazwa musi być unikalna).
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
          <Button size="sm" disabled={!name.trim()} loading={mutation.isPending} onClick={submit}>
            {preset ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── one editable layer row (commits on blur / change) ────────────────────────
function LayerRow({
  layer,
  etLookup,
  onUpdate,
  onRemove,
}: {
  layer: DxfLayerCatalogEntry;
  etLookup: Map<string, ZEElementType>;
  onUpdate: (payload: Partial<DxfLayerCatalogEntry>) => void;
  onRemove: () => void;
}) {
  const [layerName, setLayerName] = useState(layer.layer_name);
  const [semanticType, setSemanticType] = useState(layer.semantic_type);

  return (
    <tr className="border-t border-[var(--color-border-muted)]">
      <td className="px-2 py-1.5">
        <input
          className="input-base text-xs py-1 font-mono"
          value={layerName}
          onChange={(e) => setLayerName(e.target.value)}
          onBlur={() => {
            const v = layerName.trim();
            if (v && v !== layer.layer_name) onUpdate({ layer_name: v });
            else setLayerName(layer.layer_name);
          }}
        />
      </td>
      <td className="px-2 py-1.5">
        <select
          className="input-base text-xs py-1"
          value={layer.kind}
          onChange={(e) => onUpdate({ kind: e.target.value as DxfLayerKind })}
        >
          <option value="line">{KIND_LABEL.line}</option>
          <option value="object">{KIND_LABEL.object}</option>
        </select>
      </td>
      <td className="px-2 py-1.5">
        <input
          className="input-base text-xs py-1 font-mono"
          list="dxf-semantic-suggestions"
          value={semanticType}
          onChange={(e) => setSemanticType(e.target.value)}
          onBlur={() => {
            const v = semanticType.trim();
            if (v && v !== layer.semantic_type) onUpdate({ semantic_type: v });
            else setSemanticType(layer.semantic_type);
          }}
        />
        <SemanticVerdict value={semanticType} etLookup={etLookup} />
      </td>
      <td className="px-2 py-1.5">
        <select
          className="input-base text-xs py-1"
          value={layer.status}
          onChange={(e) => onUpdate({ status: e.target.value as DxfLayerStatus })}
          title="Co się z urządzeniami na tej warstwie dzieje — z tego pola dobierają się formularze zgody WPW"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {!layer.status && (
          <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
            {inferStatusLabel(layer.layer_name, layer.semantic_type)}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 text-center">
        <input
          type="checkbox"
          checked={layer.is_active}
          onChange={(e) => onUpdate({ is_active: e.target.checked })}
        />
      </td>
      <td className="px-2 py-1.5 text-right">
        <button
          type="button"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          title="Usuń warstwę"
          onClick={onRemove}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

// ── layers editor for one preset ──────────────────────────────────────────────
function PresetLayersEditor({ preset }: { preset: DxfLayerPreset }) {
  const { data: layers, isLoading } = useDxfLayers(preset.id, false);
  const m = useDxfLayerMutations(preset.id);
  const { data: elementTypes } = useZEElementTypes();
  const etLookup = useMemo(() => buildEtLookup(elementTypes), [elementTypes]);

  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState<DxfLayerKind>("line");
  const [newSemantic, setNewSemantic] = useState("");
  const [newStatus, setNewStatus] = useState<DxfLayerStatus>("");

  const addLayer = () => {
    const name = newName.trim();
    const semantic = newSemantic.trim();
    if (!name || !semantic) return;
    m.create.mutate(
      {
        preset: preset.id, layer_name: name, kind: newKind, semantic_type: semantic,
        status: newStatus, is_active: true,
      },
      { onSuccess: () => { setNewName(""); setNewSemantic(""); setNewStatus(""); } },
    );
  };

  return (
    <SectionCard title={`Warstwy presetu: ${preset.name}`}>
      <datalist id="dxf-semantic-suggestions">
        {(elementTypes ?? []).map((t) => (
          <option key={t.code} value={t.code} label={`${t.label} (${KIND_LABEL[t.kind as DxfLayerKind] ?? t.kind})`} />
        ))}
        {(elementTypes ?? [])
          .filter((t) => t.preset_key && t.preset_key !== t.code)
          .map((t) => (
            <option key={`pk-${t.code}`} value={t.preset_key} label={t.label} />
          ))}
        {SEMANTIC_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
      </datalist>

      <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mb-3">
        Zmapuj nazwy warstw z DXF na ich znaczenie. <b>Linie</b> na warstwach typu „Linia" tworzą trasę,
        a punkty/bloki na warstwach typu „Obiekt" — obiekty na mapie. Warstwy nieaktywne są pomijane.
        Typ semantyczny zgodny z katalogiem typów ZE (kod lub klucz presetowy) dostaje na mapie
        stylowanie jak na rysunku koncepcji przetargu. Po zmianie mapowania użyj „Przebuduj podgląd"
        na pojedynczej wersji trasy albo „Przeładuj projekty" przy presecie, aby rozpoznać na nowo
        wszystkie projekty z niego korzystające (bez wgrywania map od nowa).
      </p>

      {isLoading ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left px-2 py-1.5 section-label">Nazwa warstwy DXF</th>
                <th className="text-left px-2 py-1.5 section-label">Rodzaj</th>
                <th className="text-left px-2 py-1.5 section-label">Typ semantyczny</th>
                <th className="text-left px-2 py-1.5 section-label">Status</th>
                <th className="px-2 py-1.5 section-label">Aktywna</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {(layers ?? []).map((l) => (
                <LayerRow
                  key={l.id}
                  layer={l}
                  etLookup={etLookup}
                  onUpdate={(payload) => m.update.mutate({ id: l.id, payload })}
                  onRemove={() => m.remove.mutate(l.id)}
                />
              ))}
              {/* add-row */}
              <tr className="border-t-2 border-[var(--color-border)]">
                <td className="px-2 py-1.5">
                  <input
                    className="input-base text-xs py-1 font-mono"
                    placeholder="np. LINIA_KABLOWA"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addLayer()}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <select
                    className="input-base text-xs py-1"
                    value={newKind}
                    onChange={(e) => setNewKind(e.target.value as DxfLayerKind)}
                  >
                    <option value="line">{KIND_LABEL.line}</option>
                    <option value="object">{KIND_LABEL.object}</option>
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <input
                    className="input-base text-xs py-1 font-mono"
                    list="dxf-semantic-suggestions"
                    placeholder="np. trasa_kablowa"
                    value={newSemantic}
                    onChange={(e) => setNewSemantic(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addLayer()}
                  />
                  <SemanticVerdict value={newSemantic} etLookup={etLookup} />
                </td>
                <td className="px-2 py-1.5">
                  <select
                    className="input-base text-xs py-1"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as DxfLayerStatus)}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </td>
                <td colSpan={2} className="px-2 py-1.5 text-right">
                  <Button size="sm" disabled={!newName.trim() || !newSemantic.trim()} loading={m.create.isPending} onClick={addLayer}>
                    + Dodaj warstwę
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
          {(layers ?? []).length === 0 && (
            <p className="text-[11px] font-mono text-[var(--color-text-muted)] mt-2">
              Brak warstw — dodaj pierwszą poniżej.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
/* Zakładka „Warstwy DXF" strony Presety (/power-projects/presety?tab=dxf). */
export function DxfPresetsSection() {
  const { data: presets, isLoading } = useDxfLayerPresets();
  const m = useDxfPresetMutations();
  const user = useAuthStore((s) => s.user);
  // Mirrors the backend gate on reload-projects (IsDirectorOrCEO).
  const canReloadProjects = !!user && (user.is_ceo || user.role === "director");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<DxfLayerPreset | null>(null);
  const [creating, setCreating] = useState(false);
  const [reloading, setReloading] = useState<DxfLayerPreset | null>(null);

  const selected = (presets ?? []).find((p) => p.id === selectedId) ?? presets?.[0] ?? null;

  return (
    <div className="space-y-4">
      <SectionToolbar
        text="Nazwy warstw różnią się między biurami/operatorami — projektant wybiera preset przy wgrywaniu mapy."
        actions={<Button size="sm" onClick={() => setCreating(true)}>+ Nowy preset</Button>}
      />

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (presets ?? []).length === 0 ? (
        <EmptyState>
          Brak presetów. Utwórz pierwszy preset (np. „ENERGA — standard") i dodaj mapowania warstw.
        </EmptyState>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr] gap-5 items-start">
          <SectionCard title="Presety">
            <ul className="space-y-1">
              {(presets ?? []).map((p) => {
                const isSel = selected?.id === p.id;
                return (
                  <li key={p.id}>
                    <div
                      className={`border px-3 py-2 cursor-pointer transition-colors ${
                        isSel
                          ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                          : "border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
                      }`}
                      onClick={() => setSelectedId(p.id)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm text-[var(--color-text-primary)]">
                          {p.name}
                          {!p.is_active && <span className="section-label ml-1">(nieaktywny)</span>}
                        </span>
                      </div>
                      <p className="text-[11px] font-mono text-[var(--color-text-muted)] mt-0.5">
                        {p.line_count} linii · {p.object_count} obiektów
                      </p>
                      {p.description && (
                        <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 leading-snug">{p.description}</p>
                      )}
                      {isSel && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(p); }}>
                            Edytuj
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Utwórz kopię presetu wraz ze wszystkimi warstwami"
                            loading={m.duplicate.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              m.duplicate.mutate(p.id, { onSuccess: (copy) => setSelectedId(copy.id) });
                            }}
                          >
                            Duplikuj
                          </Button>
                          {canReloadProjects && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Rozpoznaj ponownie wszystkie projekty z tego presetu (bez wgrywania map od nowa)"
                              onClick={(e) => { e.stopPropagation(); setReloading(p); }}
                            >
                              Przeładuj projekty
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Usunąć preset „${p.name}" wraz z warstwami?`)) {
                                m.remove.mutate(p.id);
                                setSelectedId(null);
                              }
                            }}
                          >
                            Usuń
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </SectionCard>

          {selected ? (
            <PresetLayersEditor preset={selected} />
          ) : (
            <SectionCard title="Warstwy">
              <EmptyState>Wybierz preset z listy, aby edytować jego warstwy.</EmptyState>
            </SectionCard>
          )}
        </div>
      )}

      {(creating || editing) && (
        <PresetModal preset={editing} onClose={() => { setCreating(false); setEditing(null); }} />
      )}
      {reloading && (
        <ReloadPresetProjectsDialog preset={reloading} onClose={() => setReloading(null)} />
      )}
    </div>
  );
}
